import React from "react";
import { Animate } from "react-move";

import { Opinion, OpinionVote } from "@/service/Model";
import { easeBounceOut } from "@/utils/animation";
import { scaleRange } from "@/utils/transform";

import {
  buckets,
  centerPoint,
  getAnswerBucket,
  maxAnswer,
  minAnswer,
  percentageGutter,
  Point,
  Result,
} from "../MeterConstants";

export type MeterOpinionProps = {
  meter: Opinion;
  results: Result[];
  onClickOpinion: (index: number) => void;
};

const MeterOpinion: React.FC<MeterOpinionProps> = ({
  meter,
  results,
  onClickOpinion,
}) => {
  const imageQuery = "?width=174&height=174";

  const getOpinionPosition = (opinion: OpinionVote): Point => {
    const answerBucket = getAnswerBucket(
      scaleRange(opinion.opinion, 0, 180, minAnswer, maxAnswer)
    );
    const x = buckets[answerBucket];
    const result = results[answerBucket];
    const y = centerPoint.y - (result ? result.size : 0) - percentageGutter;
    return { x, y };
  };

  return (
    <g>
      {(meter.opinions || []).map((opinion: OpinionVote, index: number) => {
        const position = getOpinionPosition(opinion);
        return (
          <Animate
            key={index.toString()}
            start={{
              y: position.y,
            }}
            update={{
              y: [position.y],
              timing: {
                duration: 300,
                ease: easeBounceOut,
              },
            }}
          >
            {(animation: { y: number }) => (
              <g
                transform={`translate(${position.x}, ${animation.y})`}
                key={index.toString()}
              >
                <g className="meterResult">
                  <defs>
                    <clipPath id="roundedClip">
                      <circle r={30} />
                    </clipPath>
                  </defs>
                  <image
                    preserveAspectRatio="xMidYMid slice"
                    clipPath="url(#roundedClip)"
                    xlinkHref={opinion.iconUrl + imageQuery}
                    x={-32}
                    y={-32}
                    width={64}
                    height={64}
                    data-qa="opinion:image"
                    onDragStart={(e: React.MouseEvent) => e.preventDefault()}
                    onMouseDown={(e: React.MouseEvent) =>
                      e.nativeEvent.stopImmediatePropagation()
                    }
                    onMouseMove={(e: React.MouseEvent) =>
                      e.nativeEvent.stopImmediatePropagation()
                    }
                    onMouseUp={(e: React.MouseEvent) =>
                      e.nativeEvent.stopImmediatePropagation()
                    }
                    onTouchStart={(e: React.TouchEvent) =>
                      e.nativeEvent.stopImmediatePropagation()
                    }
                    onTouchMove={(e: React.TouchEvent) =>
                      e.nativeEvent.stopImmediatePropagation()
                    }
                    onTouchEnd={(e: React.TouchEvent) =>
                      e.nativeEvent.stopImmediatePropagation()
                    }
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.nativeEvent.stopImmediatePropagation();
                      onClickOpinion(index);
                    }}
                  />
                </g>
              </g>
            )}
          </Animate>
        );
      })}
    </g>
  );
};

export default MeterOpinion;
